import { ConnectError } from "@connectrpc/connect";
import { consoleLinks, errorInfoOf, saasErrorOf, type ConsoleLinks, type SaasError } from "@fox1ck-org/auth/saas";
import { toast } from "@fox1ck-org/ui/toast";

import { runtimeConfig } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { formatInt } from "@/lib/format";
import { t, type TKey } from "@/lib/i18n";

/**
 * The org's plan, as the tracker shows it (card S11b; contracts.md §4–5).
 * Entitlements are console's (`useEntitlements`); what is used is the tracker's
 * own count, the same the server's `admit` counts against the limit
 * (internal/repository/plan_usage.go): campaigns not in the archive, every
 * tracking domain. A refusal — `limit_reached`, `feature_not_in_plan` — is a
 * neutral toast with the way forward, never a red error: the person has done
 * nothing wrong, the plan just ends here.
 */

/** Hard meters of the catalog (catalog/products/tracker.yaml). */
export type TrackerMeter = "tracker.campaigns" | "tracker.domains";

const METER_LABEL: Record<string, TKey> = {
  "tracker.campaigns": "plan.meter.tracker.campaigns",
  "tracker.domains": "plan.meter.tracker.domains",
};

const FEATURE_LABEL: Record<string, TKey> = {
  "tracker.reports.custom": "plan.feature.tracker.reports.custom",
  "tracker.postbacks.s2s": "plan.feature.tracker.postbacks.s2s",
};

/** A meter's name in the buyer's words; an unknown key as it is. Pure. */
export function meterLabel(meter: string): string {
  const k = METER_LABEL[meter];
  return k ? t(k) : meter;
}

/** A feature's name in the buyer's words; an unknown key as it is. Pure. */
export function featureLabel(feature: string): string {
  const k = FEATURE_LABEL[feature];
  return k ? t(k) : feature;
}

/** console's customer screens, from the runtime config — never a hardcoded host. */
export const links: ConsoleLinks = consoleLinks(runtimeConfig.consoleUrl);

/** What a refusal says and where it leads. */
export interface Refusal {
  error: SaasError;
  title: string;
  description?: string;
  action?: { label: string; href: string };
}

/** The refusal an error is, in the tracker's words; null for any other failure. Pure. */
export function refusalOf(err: unknown, to: ConsoleLinks = links): Refusal | null {
  const error = saasErrorOf(err);
  if (!error) return null;
  switch (error.kind) {
    case "limit_reached": {
      const meter = meterLabel(error.meter);
      return {
        error,
        title: error.limit === null ? t("plan.refusal.limitPlain", { meter }) : t("plan.refusal.limit", { meter, limit: formatInt(error.limit) }),
        description: t("plan.refusal.limit.hint"),
        action: { label: t("plan.increase"), href: to.billing({ meter: error.meter || undefined }) },
      };
    }
    case "feature_not_in_plan":
      return {
        error,
        title: t("plan.refusal.feature", { feature: featureLabel(error.feature) }),
        description: t("plan.refusal.feature.hint"),
        action: { label: t("plan.billing"), href: to.billing({ feature: error.feature || undefined }) },
      };
    case "product_not_in_plan":
      return { error, title: t("plan.refusal.product", { product: error.product }), action: { label: t("plan.connect"), href: to.connect(error.product) } };
    case "org_suspended":
      return { error, title: t("plan.refusal.suspended"), action: { label: t("plan.billing"), href: to.billing() } };
    case "org_ambiguous":
      return { error, title: t("plan.refusal.ambiguous") };
  }
}

/** One toast per refusal kind and key: a double click does not stack two. */
function refusalId(r: Refusal): string {
  const e = r.error;
  return `refusal:${e.kind}:${"meter" in e ? e.meter : "feature" in e ? e.feature : "product" in e ? e.product : ""}`;
}

/** The server's reason when a write touches a campaign another block holds the pen of. */
export const LOCKED_BY_SOURCE = "locked_by_source";

/** The write was refused because the campaign is managed by its import source. Pure. */
export function isLockedBySource(err: unknown): boolean {
  return errorInfoOf(err)?.reason === LOCKED_BY_SOURCE;
}

/**
 * Show a refusal as a neutral toast with its button; true when `err` was one.
 * A campaign managed by its import source is one too — neutral, nothing to buy.
 * The button opens console in a new tab, so the screen — a half-filled form
 * included — stays as it was.
 */
export function showRefusal(err: unknown): boolean {
  if (isLockedBySource(err)) {
    toast(t("plan.refusal.lockedBySource"), { id: `refusal:${LOCKED_BY_SOURCE}` });
    return true;
  }
  const r = refusalOf(err);
  if (!r) return false;
  toast(r.title, {
    id: refusalId(r),
    description: r.description,
    duration: 12_000,
    action: r.action ? { label: r.action.label, onClick: () => window.open(r.action!.href, "_blank", "noopener") } : undefined,
  });
  return true;
}

/**
 * The one way a write's failure reaches a toast: a refusal of the plan is the
 * neutral toast with «Увеличить» / «Тариф и оплата»; anything else is the red
 * one with the server's own cause, or `fallback` when it says nothing.
 */
export function toastFailure(err: unknown, fallback: string): void {
  if (showRefusal(err)) return;
  // A Connect failure with no cause of its own says only its code («[internal]»): the screen's sentence then.
  toast.error((err instanceof ConnectError ? err.rawMessage : errorText(err)) || fallback);
}

/** How much of a hard meter the org uses against its plan. */
export interface MeterUsage {
  meter: TrackerMeter;
  used: number | undefined;
  /** null — the meter is not in the plan (or entitlements did not answer); Infinity — no ceiling. */
  limit: number | null;
  /** A finite ceiling: the only case worth a line on screen. */
  limited: boolean;
  /** used ≥ limit: the next create is refused. */
  full: boolean;
  loading: boolean;
}

/** `used` against `limit`. Pure. */
export function meterUsage(meter: TrackerMeter, used: number | undefined, limit: number | null, loading = false): MeterUsage {
  const limited = limit !== null && Number.isFinite(limit);
  return { meter, used, limit, limited, full: limited && used !== undefined && used >= (limit as number), loading };
}
